import { useCallback, useEffect, useState } from 'react';
import Lottie from 'react-lottie-player';

import useUpdateEffect from 'hooks/useUpdateEffect';

const cache = {};

/**
 * @author @buunsal
 *
 * LottiePlayer is a React component that renders a Lottie animation.
 * It fetches the animation data from a remote server and caches it for future use.
 * The component uses the react-lottie-player library to render the animation.
 * @param {Object} props - The props for the LottiePlayer component.
 * @param {string} [props.name] - The name of the lottie file. If you provide a name, the component will fetch the lottie file from 'https://tryoto-public.storage.googleapis.com/lottie-files-compressed/'.
 * @param {boolean} [props.play = true] - If true, the animation will be played.
 * @param {number} [props.speed] - The speed of the animation.
 * @param {boolean} [props.loop = true] - If true, the animation will loop.
 * @param {string} [props.width = "120px"] - The width of the lottie container.
 * @param {string} [props.height = "120px"] - The height of the lottie container.
 * @param {Object} [props.style] - The style of the lottie container.
 */
const LottiePlayer = (props) => {
	const { name, play = true, loop = true, width, height, style, ...moreProps } = props;

	if (!name) {
		throw new Error('LottiePlayer: You must provide a name');
	}

	const [data, setData] = useState(cache[name]);

	const fetchLottieJson = useCallback(async () => {
		const lottieJson = await fetch(
			`https://tryoto-public.storage.googleapis.com/lottie-files-compressed/${name}.json.gz`
		).then((response) => response.json());
		cache[name] = lottieJson;
		setData(lottieJson);
	}, []);

	useEffect(() => {
		if (!data) {
			fetchLottieJson();
		}
	}, []);

	useUpdateEffect(() => {
		fetchLottieJson();
	}, [name]);

	return (
		<Lottie
			animationData={data}
			play={play}
			loop={loop}
			style={{
				width: width ?? 120,
				height: height ?? 120,
				...style
			}}
			{...moreProps}
		/>
	);
};

export default LottiePlayer;
